import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { OtpUser } from '../../common/models';
import { RootState } from '../store';
import { AxiosInstance } from 'axios';
import { jwtDecode, JwtPayload } from 'jwt-decode';

const LAW_ENFORCEMENT_IDP_TYPE = 'aao';
const LAW_ENFORCEMENT_ORGANIZATION_TYPE = 'lea';
export interface CsrUserJwtPayload extends JwtPayload {
  name?: string;
  sms_number?: string;
  is_guest?: boolean;
}

export interface KeycloakJwtPayload extends JwtPayload {
  email?: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  gender?: string;
  albertaVerified?: string;
  dobTemp?: string;
  driverLicenceTemp?: string;
  identityType?: string;
  organizationId?: string;
  organizationType?: string;
  organizationName?: string;
  organizationCode?: string;
  jobTitle?: string;
  userRole?: string;
  birthdate?: string;
  ABDLNo?: string;
}

export interface csrUser {
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  is_law: boolean;
  is_law_admin: boolean;
  is_tec: boolean;
  org: string;
  org_name: string;
  org_code: string;
  le_district: string;
  le_agency: string;
  le_badge: string;
  ecollision_id: number;
  keycloak_id: string;
  idp: string;
}

export const getLoginInfoFromJwt = (jwtObject: any) => {
  return jwtDecode<CsrUserJwtPayload>(jwtObject);
};

export const getLoginInfoFromKeycloakJwt = (jwtObject: any) => {
  return jwtDecode<KeycloakJwtPayload>(jwtObject);
};

export const isTheAccountVerifiedByGoA = (
  albertaVerified: string | null | undefined,
): boolean => {
  if (!albertaVerified) {
    return false;
  }
  const BasicVerified = 'loa2';
  const AccountVerified = 'loa3';

  return albertaVerified.toLowerCase().endsWith(AccountVerified);
};

export const isAdmin = (stateAuth: AuthenticationState): boolean => {
  if (
    isUserLoggedIn(stateAuth) &&
    stateAuth.auth.csrUser &&
    stateAuth.auth.csrUser.is_tec
  ) {
    return true;
  }
  return false;
};

export const isLawEnforcementAdmin = (
  stateAuth: AuthenticationState,
): boolean => {
  if (
    isUserLoggedIn(stateAuth) &&
    stateAuth.auth.csrUser &&
    stateAuth.auth.csrUser.idp === LAW_ENFORCEMENT_IDP_TYPE &&
    stateAuth.auth.csrUser.is_law_admin
  ) {
    return true;
  }
  return false;
};

export const isLawEnforcementOfficer = (
  stateAuth: AuthenticationState,
): boolean => {
  if (
    isUserLoggedIn(stateAuth) &&
    stateAuth.auth.csrUser &&
    stateAuth.auth.csrUser.idp === LAW_ENFORCEMENT_IDP_TYPE &&
    stateAuth.auth.csrUser.is_law
  ) {
    return true;
  }
  return false;
};

export const isLawEnforcementConfigured = (
  stateAuth: AuthenticationState,
): boolean => {
  if (
    isUserLoggedIn(stateAuth) &&
    stateAuth.auth.csrUser &&
    stateAuth.auth.csrUser.le_agency &&
    stateAuth.auth.csrUser.le_agency !== '' &&
    stateAuth.auth.csrUser.le_district &&
    stateAuth.auth.csrUser.le_district !== '' &&
    stateAuth.auth.csrUser.le_badge &&
    stateAuth.auth.csrUser.le_badge !== ''
  ) {
    return true;
  }

  return false;
};

export const isUserLoggedIn = (stateAuth: AuthenticationState) => {
  return stateAuth.auth.access && stateAuth.auth.access.access;
};

export interface AuthenticationState {
  auth: {
    isGuest?: boolean;
    csrUser?: csrUser;
    isKeycloak?: boolean;
    access: {
      access: string;
      refresh: string;
    };
  };
  isLoggedIn: boolean;
  username: string;
  isLoading: boolean;
  error: string | null;
  loggedOut: boolean;
}

const getTokens = (): {
  access: string;
  refresh: string;
} => {
  let accessToken = '';
  let refreshToken = '';
  const tokens = localStorage.getItem('access');

  if (tokens) {
    const jsonObject = JSON.parse(tokens);
    accessToken = jsonObject.access?.access;
    refreshToken = jsonObject.access?.refresh;
  }

  return { access: accessToken, refresh: refreshToken };
};

const getEmail = (): string => {
  let email = '';
  const tokens = localStorage.getItem('access');
  if (tokens) {
    const jsonObject = JSON.parse(tokens);
    if (jsonObject) {
      let keycloakPayload = getLoginInfoFromKeycloakJwt(
        jsonObject.access.access,
      );
      email = keycloakPayload.email ?? '';
    }
  }
  return email;
};

const getUserName = (): string => {
  let userName = '';
  const tokens = localStorage.getItem('access');
  if (tokens) {
    const jsonObject = JSON.parse(tokens);
    if (jsonObject) {
      let keycloakPayload = getLoginInfoFromKeycloakJwt(
        jsonObject.access.access,
      );
      userName = keycloakPayload.email ?? '';
    }
  }

  return userName;
};

export const initialState: AuthenticationState = {
  auth: {
    isGuest: false,
    csrUser: undefined,
    isKeycloak: false,
    access: getTokens(),
  },
  loggedOut: false,
  isLoggedIn: false,
  username: getUserName(),
  isLoading: false,
  error: null,
};

export const createUser = createAsyncThunk(
  'auth/createUser',
  async ({
    csrService,
    smsCreateRequest,
  }: {
    csrService: AxiosInstance;
    smsCreateRequest: any;
  }) => {
    console.log('Creating user with request: ', smsCreateRequest);
    const user = await csrService.post('/api/v1/users/', smsCreateRequest);

    return user.data;
  },
);

export const loadCsrUser = createAsyncThunk(
  'auth/loadCsrUser',
  async ({ csrService }: { csrService: AxiosInstance }, {}) => {
    const user = await csrService.get(`/api/v1/user/`);

    return user.data;
  },
);

export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async (
    {
      csrService,
      otpUser,
    }: {
      csrService: AxiosInstance;
      otpUser: OtpUser;
    },
    { getState, dispatch },
  ) => {
    const user = await csrService.post('/api/v1/users/otp/', otpUser);

    dispatch(setTokens(user.data));

    return user.data;
  },
);

export const sendOtp = createAsyncThunk(
  'auth/sendOtp',
  async ({
    csrService,
    otpUser,
  }: {
    csrService: AxiosInstance;
    otpUser: any;
  }) => {
    const user = await csrService.post('/api/v1/users/otp/renew/', otpUser);

    return user.data;
  },
);

export const refreshJwt = createAsyncThunk(
  'auth/renewJwt',
  async (
    {
      csrService,
    }: {
      csrService: AxiosInstance;
    },
    { getState, dispatch },
  ) => {
    const state = getState() as RootState;
    console.log('State', state);

    if (
      state.auth.auth &&
      state.auth.auth!.access &&
      state.auth.auth!.isGuest
    ) {
      const user = await csrService.post('/api/v1/users/jwt/refresh/', {
        refresh: state.auth.auth.access!.refresh,
      });

      dispatch(setTokens(user.data));
      return user.data;
    }

    return null;
  },
);

export const emailUserInformation = createAsyncThunk(
  'auth/emailUserInformation',
  async (
    {
      csrService,
      emailRequest: emailRequest,
    }: {
      csrService: AxiosInstance;
      emailRequest: any;
    },
    { getState, dispatch },
  ) => {
    const state = getState() as RootState;
    console.log('State', state);

    if (state.auth.auth && state.auth.auth!.access && state.auth.auth.isGuest) {
      const user = await csrService.post('/api/v1/users/email/', emailRequest);

      return user.data;
    }

    return null;
  },
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearLogoutFlag: (state) => {
      state.loggedOut = false;
    },
    logout: (state) => {
      localStorage.removeItem('access');
      localStorage.clear();
      sessionStorage.removeItem('reduxState');
      sessionStorage.clear();
      state.loggedOut = true;
      state.auth = {
        isGuest: false,
        isKeycloak: false,
        csrUser: undefined,
        access: {
          access: '',
          refresh: '',
        },
      };

      console.log('Local Storage:', localStorage);
      console.log('Session Storage:', sessionStorage);

      //alert('do logout url')
      //To logout a user we must also navigate to a URL
      // const baseUrl = import.meta.env.REACT_APP_APP_URL;

      // // TODO : make this base url an environment variable.
      // const logoutUrl =
      //   'https://access-uat.alberta.ca/auth/realms/e55c98bb-19eb-47e1-acce-d86c5e2fe490/protocol/openid-connect/logout?redirect_uri=' +
      //   baseUrl +
      //   '/logout';

      //window.location.href = logoutUrl;

      //return state;
    },
    setTokens: (state, action: PayloadAction<any>) => {
      if (action.payload.jwt) {
        console.log('setting tokens: ', action.payload);

        localStorage.setItem('access', JSON.stringify(action.payload.jwt));

        const auth = {
          access: action.payload.jwt.access,
          refresh: action.payload.jwt.refresh,
        };
        state.auth = auth;
        state.isLoggedIn = true;
        state.username = '';

        return state;
      }
    },

    setAuthFromKeycloak(state, action: PayloadAction<any>) {
      console.log('setting tokens from keycloak: ', action.payload);

      localStorage.setItem('access', JSON.stringify(action.payload));

      state.auth = action.payload;
      state.isLoggedIn = true;
      state.username = '';

      return state;
    },

    setLoginStatus: (state, action: PayloadAction<boolean>) => {
      state.isLoggedIn = action.payload;
    },
    setUsername: (state, action: PayloadAction<string>) => {
      state.username = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createUser.fulfilled, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(createUser.rejected, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        state.error = null;
        const auth = {
          isGuest: true,
          access: {
            access: action.payload.jwt.access,
            refresh: action.payload.jwt.refresh,
          },
        };

        state.auth = auth;
      })
      .addCase(loginUser.rejected, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(sendOtp.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(sendOtp.fulfilled, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(sendOtp.rejected, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(refreshJwt.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(refreshJwt.fulfilled, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        state.error = null;
        state.auth!.access = action.payload;
      })
      .addCase(refreshJwt.rejected, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(emailUserInformation.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        emailUserInformation.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
          state.error = null;
          state.auth!.access = action.payload;
        },
      )
      .addCase(
        emailUserInformation.rejected,
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
          state.error = action.payload;
        },
      )
      .addCase(loadCsrUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loadCsrUser.fulfilled, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        state.error = null;
        if (action.payload) {
          state.auth.csrUser = action.payload;
        }
      })
      .addCase(loadCsrUser.rejected, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { logout, setTokens, setAuthFromKeycloak, clearLogoutFlag } =
  authSlice.actions;

export default authSlice.reducer;
